import { ReadyState, Store } from './store';
import io, { Socket as IOSocket } from 'socket.io-client';
import { makeObservable, observable } from 'mobx';
import { BaseStore } from './baseStore';
import { ChannelStoreState } from './ChannelStore';
import { showCustomToast } from '../components/notifications/CustomToast';

export enum SocketStoreState {
  NOT_INITIALIZED = 1 << 0,
  CONNECTED = 1 << 1,
  SOCKETID = 1 << 2,
}

export class SocketStore extends BaseStore<SocketStoreState> {
  ws: IOSocket | undefined | null;
  socketId?: string | undefined | null;
  e2eeRegisteredOnce = false;

  constructor(store: Store) {
    super(SocketStoreState.NOT_INITIALIZED, store, 'blue');
    makeObservable(this, {
      ws: observable,
      socketId: observable,
      e2eeRegisteredOnce: observable,
    });
    this.log('Init WS endpoint');

    this.setupWindowCloseListener();

    if (store.token != null) this.reload();
  }

  private setupWindowCloseListener(): void {
    window.addEventListener('beforeunload', () => this.setOfflineOnWindowClose());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.setOfflineOnWindowClose();
    });
  }

  private async restoreStatusFromDatabase(): Promise<void> {
    try {
      const response = await fetch(`${this.store!.apiURL}/user/@me`, {
        headers: { Authorization: `Bearer ${this.store!.token}` },
      });

      if (response.ok) {
        const userData = (await response.json()).data;
        this.store!.user.updateUser(userData);
        this.log(`Status restored from DB: "${userData.status}"`);
      }
    } catch (error) {
      this.log('Failed to restore status from DB:', error);
    }
  }

  private async setOfflineInDatabase(): Promise<void> {
    const user = this.store!.user.user;
    if (!user || user.status === 'offline') return;

    try {
      await fetch(`${this.store!.apiURL}/user/@me`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.store!.token}`,
        },
        body: JSON.stringify({ status: 'offline' }),
      });

      this.store!.user.updateUser({ status: 'offline' });
      this.log('Status set to offline in MongoDB');
    } catch (error) {
      this.log('Failed to set offline in MongoDB:', error);
    }
  }

  private setOfflineOnWindowClose(): void {
    const user = this.store!.user.user;
    if (!user || user.status === 'offline') return;

    fetch(`${this.store!.apiURL}/user/@me`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.store!.token}`,
      },
      body: JSON.stringify({ status: 'offline' }),
      keepalive: true,
    }).catch(() => this.log('Failed to send offline status'));

    this.store!.user.updateUser({ status: 'offline' });
  }



  showMessageNotification(data: any, channelId: number): void {
    try {
      const channel = this.store!.channels.getChannel(channelId);
      if (!channel) return;

      const senderId = data.author;
      const sender = this.store!.user.related?.find((u: any) => u.id === senderId);
      const senderName =
        sender?.displayName || sender?.name || sender?.username || `User ${senderId}`;

      let channelName = '';
      if (channel.type === 'dm') {
        const otherUser = this.getOtherUserInDM(channel);
        channelName =
          otherUser?.displayName || otherUser?.name || otherUser?.username || `User ${otherUser?.id}`;
      } else if (channel.type === 'group') {
        channelName = channel.name || `Group ${channel.id}`;
      }

      const message = data.content || '';
      const notificationText = `${senderName} w ${channelName}: ${message}`;

      if (typeof window !== 'undefined' && 'Notification' in window) {
        if (Notification.permission === 'granted') {
          new Notification(`Nowa wiadomość`, {
            body: notificationText,
            icon: '/favicon.ico',
            tag: `message-${channelId}`,
          });
        }
      }
    } catch (e) {
      this.log('Failed to show message notification', e);
    }
  }

  private getOtherUserInDM(channel: any): any {
    const currentUserId = this.store!.user.user?.id;
    if (!channel.members || !Array.isArray(channel.members)) return null;

    const otherUserId = channel.members.find((id: number) => id !== currentUserId);
    if (!otherUserId) return null;

    return this.store!.user.related?.find((u: any) => u.id === otherUserId);
  }

  private requestNotificationPermission(): void {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission().then(permission => {
          this.log('Notification permission:', permission);
        });
      }
    }
  }

  reload() {
    this.setState(SocketStoreState.NOT_INITIALIZED);
    this.socketId = undefined;
    this.ws = null;

    this.ws = io(this.store!.wsURL.toString(), {
      transports: ['websocket'],
      autoConnect: false,
      auth: { authorization: 'Bearer ' + this.store!.token },
    });

    this.ws.on('disconnect', () => {
      this.socketId = null;
      this.removeState(SocketStoreState.CONNECTED);
      this.store!.removeState(ReadyState.SOCKETID);
      this.setOfflineInDatabase();
    });

    this.ws.on('connect', () => {
      this.addState(SocketStoreState.CONNECTED);
    });

    this.ws.on('client:id', data => {
      this.socketId = data.socketId;
      this.store!.addState(ReadyState.SOCKETID);
      this.addState(SocketStoreState.SOCKETID);
    });
    this.ws.on('client:identification', data => {
      this.log('Received user identification from socket:', data);
      if (this.store!.user.user) {
        this.store!.user.updateUser(data);
      } else {
        this.store!.user.setUser(data);
      }
      this.restoreStatusFromDatabase();
    });
    this.ws.on('client:session', async (data) => {
      this.store.sessions.setActiveSession(data);
      this.store!.addState(ReadyState.SESSION);
      this.restoreStatusFromDatabase();
      if (!this.store!.e2ee.isReady()) {
        await this.store!.e2ee.reload(); // spróbuj wczytać klucze z localStorage
      }
    });

    this.ws.on("channel:message", async (data) => {
      try {

        this.store.channels.addMessage(Number(data.channel), data);
      } catch (e) {
        this.log("❌ Failed to handle channel:message", e);
      }
    });

    if (this.store!.token !== null) {
      this.ws.connect();
      this.requestNotificationPermission();
    }
  }


  logout() {
    this.socketId = undefined;
    this.ws?.disconnect();
    this.ws?.close();
    this.ws = null;
    this.setState(SocketStoreState.NOT_INITIALIZED);
  }

  isReady(): boolean {
    return this.hasState(SocketStoreState.SOCKETID) && this.hasState(SocketStoreState.CONNECTED);
  }
}
